import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControlLabel,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as CloneIcon,
  VisibilityOff as HideIcon,
  Visibility as ShowIcon,
  Add as AddQuestionIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import axios from 'axios';

const QuestionManager = () => {
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState({
    id: null,
    title: '',
    description: '',
    type: 'short_text',
    required: false,
    category_id: '',
    options: [],
    has_follow_up: false,
    follow_up_rules: [], // Array to store multiple follow-up rules
    review_guidelines: ''
  });
  const [newOption, setNewOption] = useState('');
  const [newFollowUpRule, setNewFollowUpRule] = useState({
    condition: '',
    value: '',
    question_id: ''
  });
  
  // Filter state
  const [filters, setFilters] = useState({
    category: '',
    type: '',
    required: '',
    hasFollowUp: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const categoriesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/categories`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (categoriesResponse.data.success) {
          setCategories(categoriesResponse.data.data);
        }
        
        // Fetch questions using the fetchQuestions function
        await fetchQuestions();
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    fetchData();
  }, []);

  const handleOpenDialog = (question = null) => {
    if (question) {
      // Ensure options is always an array
      const processedQuestion = {
        ...question,
        options: Array.isArray(question.options) ? question.options : 
                (question.options ? JSON.parse(JSON.stringify(question.options)) : [])
      };
      setCurrentQuestion(processedQuestion);
    } else {
      setCurrentQuestion({
        id: null,
        title: '',
        description: '',
        type: 'short_text',
        required: false,
        category_id: '',
        options: [],
        has_follow_up: false,
        follow_up_rules: [],
        review_guidelines: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === 'required' || name === 'has_follow_up') {
      setCurrentQuestion({ ...currentQuestion, [name]: checked });
    } else {
      setCurrentQuestion({ ...currentQuestion, [name]: value });
    }
  };

  const handleAddOption = () => {
    if (newOption.trim() !== '') {
      setCurrentQuestion({
        ...currentQuestion,
        options: [...(Array.isArray(currentQuestion.options) ? currentQuestion.options : []), newOption.trim()]
      });
      setNewOption('');
    }
  };

  const handleRemoveOption = (index) => {
    setCurrentQuestion({
      ...currentQuestion,
      options: currentQuestion.options.filter((_, i) => i !== index)
    });
  };

  const handleFollowUpRuleChange = (e) => {
    const { name, value } = e.target;
    setNewFollowUpRule({
      ...newFollowUpRule,
      [name]: value
    });
  };

  const handleAddFollowUpRule = () => {
    // Validate that all fields are filled
    if (!newFollowUpRule.condition || !newFollowUpRule.value || !newFollowUpRule.question_id) {
      return; // Don't add incomplete rules
    }
    
    // Add the new rule to the current question's follow_up_rules array
    // Ensure follow_up_rules is always an array before spreading
    const currentRules = Array.isArray(currentQuestion.follow_up_rules) 
      ? currentQuestion.follow_up_rules 
      : [];
      
    // Create the new rule with the correct field name for the follow-up question ID
    const newRule = {
      condition: newFollowUpRule.condition,
      value: newFollowUpRule.value,
      follow_up_question_id: newFollowUpRule.question_id,
      id: Date.now() // Add a temporary ID for tracking
    };
    
    setCurrentQuestion({
      ...currentQuestion,
      follow_up_rules: [
        ...currentRules,
        newRule
      ]
    });
    
    // Reset the new rule form
    setNewFollowUpRule({
      condition: '',
      value: '',
      question_id: ''
    });
  };

  const handleRemoveFollowUpRule = (ruleId) => {
    // Ensure follow_up_rules is always an array before filtering
    const currentRules = Array.isArray(currentQuestion.follow_up_rules) 
      ? currentQuestion.follow_up_rules 
      : [];
      
    setCurrentQuestion({
      ...currentQuestion,
      follow_up_rules: currentRules.filter(rule => rule.id !== ruleId)
    });
  };
  
  // Filter handling functions
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };
  
  const handleClearFilters = () => {
    setFilters({
      category: '',
      type: '',
      required: '',
      hasFollowUp: ''
    });
  };
  
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };
  
  // Apply filters to questions
  const filteredQuestions = questions.filter(question => {
    // Filter by category
    if (filters.category && question.category_id !== parseInt(filters.category)) {
      return false;
    }
    
    // Filter by question type
    if (filters.type && question.type !== filters.type) {
      return false;
    }
    
    // Filter by required status
    if (filters.required === 'required' && !question.required) {
      return false;
    }
    if (filters.required === 'optional' && question.required) {
      return false;
    }
    
    // Filter by follow-up status
    if (filters.hasFollowUp === 'yes' && (!question.has_follow_up || !Array.isArray(question.follow_up_rules) || question.follow_up_rules.length === 0)) {
      return false;
    }
    if (filters.hasFollowUp === 'no' && question.has_follow_up && Array.isArray(question.follow_up_rules) && question.follow_up_rules.length > 0) {
      return false;
    }
    
    return true;
  });

  // Fetch questions from the API
  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/questions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data.success) {
        // Ensure options is always an array
        const processedQuestions = response.data.data.map(question => ({
          ...question,
          options: Array.isArray(question.options) ? question.options : 
                  (question.options ? JSON.parse(JSON.stringify(question.options)) : [])
        }));
        setQuestions(processedQuestions);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };
  
  // API functions for question management
  const createQuestion = async (questionData) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/questions`, questionData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error creating question:', error);
      throw error;
    }
  };
  
  const updateQuestion = async (id, questionData) => {
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/questions/${id}`, questionData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error updating question:', error);
      throw error;
    }
  };
  
  const deleteQuestion = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/questions/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return true;
    } catch (error) {
      console.error('Error deleting question:', error);
      throw error;
    }
  };
  
  const cloneQuestion = async (id) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/questions/${id}/clone`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error cloning question:', error);
      throw error;
    }
  };

  const handleSaveQuestion = async () => {
    try {
      // Prepare the question data
      let questionData = {
        ...currentQuestion,
        options: currentQuestion.type === 'radio' || currentQuestion.type === 'checkbox' || currentQuestion.type === 'dropdown' 
          ? currentQuestion.options 
          : []
      };
      
      // Ensure follow-up rules have the correct field names
      if (questionData.follow_up_rules && Array.isArray(questionData.follow_up_rules)) {
        questionData.follow_up_rules = questionData.follow_up_rules.map(rule => ({
          condition: rule.condition,
          value: rule.value,
          question_id: rule.follow_up_question_id || rule.question_id,
          id: rule.id
        }));
      }
      
      let savedQuestion;
      
      if (currentQuestion.id) {
        // Update existing question
        savedQuestion = await updateQuestion(currentQuestion.id, questionData);
        // Update the questions list with the updated question
        setQuestions(questions.map(q => q.id === savedQuestion.id ? savedQuestion : q));
      } else {
        // Create new question
        savedQuestion = await createQuestion(questionData);
        // Add the new question to the list
        setQuestions([...questions, savedQuestion]);
      }
      
      // Show success message
      console.log('Question saved successfully:', savedQuestion);
      
      // Refresh the questions list to ensure we have the latest data
      fetchQuestions();
    } catch (error) {
      console.error('Error saving question:', error);
      // Show error message to the user
      alert(`Error saving question: ${error.response?.data?.message || error.message}`);
      return;
    }
    
    handleCloseDialog();
  };

  const handleDeleteQuestion = async (id) => {
    try {
      await deleteQuestion(id);
      // Remove question from list
      setQuestions(questions.filter(q => q.id !== id));
    } catch (error) {
      console.error('Error deleting question:', error);
    }
  };

  const handleCloneQuestion = async (question) => {
    try {
      // Clone the question using the API function
      const clonedQuestion = await cloneQuestion(question.id);
      
      // Add the cloned question to the list
      setQuestions([...questions, clonedQuestion]);
      
      // Show success message
      console.log('Question cloned successfully:', clonedQuestion);
    } catch (error) {
      console.error('Error cloning question:', error);
    }
  };

  const questionTypeOptions = [
    { value: 'short_text', label: 'Short Text' },
    { value: 'long_text', label: 'Long Text' },
    { value: 'radio', label: 'Radio Options' },
    { value: 'checkbox', label: 'Checkboxes' },
    { value: 'dropdown', label: 'Dropdown Menu' },
    { value: 'file', label: 'File Upload' }
  ];

  const followUpConditionOptions = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does Not Contain' }
  ];

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Uncategorized';
  };

  const getQuestionTypeName = (type) => {
    const questionType = questionTypeOptions.find(t => t.value === type);
    return questionType ? questionType.label : type;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h1">
          Question Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<FilterIcon />}
            onClick={toggleFilters}
            size="small"
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Question
          </Button>
        </Box>
      </Box>
      
      {/* Filter toolbar */}
      {showFilters && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 1 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Category</InputLabel>
              <Select
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                label="Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Question Type</InputLabel>
              <Select
                name="type"
                value={filters.type}
                onChange={handleFilterChange}
                label="Question Type"
              >
                <MenuItem value="">All Types</MenuItem>
                {questionTypeOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Required</InputLabel>
              <Select
                name="required"
                value={filters.required}
                onChange={handleFilterChange}
                label="Required"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="required">Required</MenuItem>
                <MenuItem value="optional">Optional</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Has Follow-up</InputLabel>
              <Select
                name="hasFollowUp"
                value={filters.hasFollowUp}
                onChange={handleFilterChange}
                label="Has Follow-up"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {filteredQuestions.length} question{filteredQuestions.length !== 1 ? 's' : ''} found
            </Typography>
            <Button 
              size="small" 
              onClick={handleClearFilters}
              disabled={!filters.category && !filters.type && !filters.required && !filters.hasFollowUp}
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>
      )}

      <Grid container spacing={3}>
        {filteredQuestions.map(question => (
          <Grid item xs={12} sm={6} md={4} key={question.id}>
            <Card>
              <CardContent>
                <Typography variant="h6" component="h2" gutterBottom>
                  {question.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {question.description}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip 
                    label={getQuestionTypeName(question.type)} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                  />
                  <Chip 
                    label={getCategoryName(question.category_id)} 
                    size="small" 
                    color="secondary" 
                    variant="outlined" 
                  />
                  {question.required && (
                    <Chip label="Required" size="small" color="error" variant="outlined" />
                  )}
                  {question.has_follow_up && (
                    <Chip label="Has Follow-up" size="small" color="info" variant="outlined" />
                  )}
                </Box>
                {(question.type === 'radio' || question.type === 'checkbox' || question.type === 'dropdown') && 
                  question.options && (Array.isArray(question.options) ? question.options.length > 0 : false) && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>
                      Options:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {(Array.isArray(question.options) ? question.options : []).map((option, index) => (
                        <Chip key={index} label={option} size="small" />
                      ))}
                    </Box>
                  </Box>
                )}
                
                {question.has_follow_up && Array.isArray(question.follow_up_rules) && question.follow_up_rules.length > 0 && (
                  <Box sx={{ mt: 1, borderLeft: '2px solid', borderColor: 'info.main', pl: 1 }}>
                    <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>
                      Follow-up Rules ({question.follow_up_rules.length}):
                    </Typography>
                    {question.follow_up_rules.map((rule, index) => (
                      <Box key={rule.id || index} sx={{ mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {index + 1}. When answer 
                          <strong>
                            {rule.condition === 'equals' ? ' equals' : 
                             rule.condition === 'not_equals' ? ' does not equal' : 
                             rule.condition === 'contains' ? ' contains' : 
                             rule.condition === 'not_contains' ? ' does not contain' : 
                             ' matches'}
                          </strong> "{rule.value}"
                        </Typography>
                        <Typography variant="body2">
                          → Show: {questions.find(q => q.id === parseInt(rule.follow_up_question_id || rule.question_id))?.title || `Question ID: ${rule.follow_up_question_id || rule.question_id}`}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
              <CardActions>
                <IconButton size="small" onClick={() => handleOpenDialog(question)}>
                  <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => handleCloneQuestion(question)}>
                  <CloneIcon />
                </IconButton>
                <IconButton size="small" onClick={() => handleDeleteQuestion(question.id)}>
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentQuestion.id ? 'Edit Question' : 'Add New Question'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="title"
                label="Question Title"
                value={currentQuestion.title}
                onChange={handleInputChange}
                fullWidth
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Category</InputLabel>
                <Select
                  name="category_id"
                  value={currentQuestion.category_id}
                  onChange={handleInputChange}
                  label="Category"
                >
                  {categories.map(category => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                value={currentQuestion.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Question Type</InputLabel>
                <Select
                  name="type"
                  value={currentQuestion.type}
                  onChange={handleInputChange}
                  label="Question Type"
                >
                  {questionTypeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mt: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="required"
                      checked={currentQuestion.required}
                      onChange={handleInputChange}
                    />
                  }
                  label="Required Question"
                />
              </Box>
            </Grid>

            {/* Options for radio, checkbox, and dropdown types */}
            {(currentQuestion.type === 'radio' || currentQuestion.type === 'checkbox' || currentQuestion.type === 'dropdown') && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Options
                  </Typography>
                  <Box sx={{ display: 'flex', mb: 2 }}>
                    <TextField
                      label="Add Option"
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                      fullWidth
                      size="small"
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddOption}
                      sx={{ ml: 1 }}
                    >
                      Add
                    </Button>
                  </Box>
                  <List dense>
                    {(Array.isArray(currentQuestion.options) ? currentQuestion.options : []).map((option, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={option} />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" onClick={() => handleRemoveOption(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Follow-up Question Settings
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    name="has_follow_up"
                    checked={currentQuestion.has_follow_up}
                    onChange={handleInputChange}
                  />
                }
                label="This question has follow-up questions"
              />
            </Grid>

            {currentQuestion.has_follow_up && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Follow-up Rules
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    {/* List of existing follow-up rules */}
                    {Array.isArray(currentQuestion.follow_up_rules) && currentQuestion.follow_up_rules.length > 0 ? (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" gutterBottom>
                          Current Rules:
                        </Typography>
                        {currentQuestion.follow_up_rules.map((rule, index) => (
                          <Box key={rule.id || index} sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1,
                            mb: 1,
                            borderRadius: 1,
                            bgcolor: 'action.hover'
                          }}>
                            <Typography variant="body2">
                              When answer 
                              <strong>
                                {rule.condition === 'equals' ? ' equals ' : 
                                 rule.condition === 'not_equals' ? ' does not equal ' : 
                                 rule.condition === 'contains' ? ' contains ' : 
                                 rule.condition === 'not_contains' ? ' does not contain ' : 
                                 ' matches '}
                              </strong>
                              "{rule.value}", show question: 
                              <strong>
                                {questions.find(q => q.id === parseInt(rule.follow_up_question_id || rule.question_id))?.title || `Question ID: ${rule.follow_up_question_id || rule.question_id}`}
                              </strong>
                            </Typography>
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleRemoveFollowUpRule(rule.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No follow-up rules added yet. Add a rule below.
                      </Typography>
                    )}
                    
                    {/* Form to add a new follow-up rule */}
                    <Typography variant="body2" gutterBottom>
                      Add New Rule:
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Condition</InputLabel>
                          <Select
                            name="condition"
                            value={newFollowUpRule.condition}
                            onChange={handleFollowUpRuleChange}
                            label="Condition"
                          >
                            {followUpConditionOptions.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          name="value"
                          label="Value"
                          value={newFollowUpRule.value}
                          onChange={handleFollowUpRuleChange}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Show Question</InputLabel>
                          <Select
                            name="question_id"
                            value={newFollowUpRule.question_id}
                            onChange={handleFollowUpRuleChange}
                            label="Show Question"
                          >
                            <MenuItem value="">Select a question</MenuItem>
                            {questions
                              .filter(q => q.id !== (currentQuestion.id || 0)) // Prevent selecting self as follow-up
                              .map(question => (
                                <MenuItem key={question.id} value={question.id}>
                                  {question.title}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleAddFollowUpRule}
                          startIcon={<AddIcon />}
                          disabled={!newFollowUpRule.condition || !newFollowUpRule.value || !newFollowUpRule.question_id}
                        >
                          Add Rule
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                  <FormHelperText>
                    You can add multiple follow-up rules to create complex conditional logic.
                  </FormHelperText>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Review Guidelines
              </Typography>
              <TextField
                name="review_guidelines"
                label="Guidelines for reviewing responses"
                value={currentQuestion.review_guidelines}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={3}
                margin="normal"
                helperText="These guidelines will be shown to admins when reviewing responses"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveQuestion} 
            variant="contained" 
            disabled={!currentQuestion.title || (
              (currentQuestion.type === 'radio' || currentQuestion.type === 'checkbox' || currentQuestion.type === 'dropdown') && 
              currentQuestion.options.length === 0
            )}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuestionManager;
